<div class="alerts-page">
  <div class="alerts-header">
    <h1>Indications</h1>

    <div class="header-actions">
      <label for="pageSize">Rows per page:</label>
      <select id="pageSize" (change)="onPageSizeChange($event)">
        <option [value]="5">5</option>
        <option [value]="10">10</option>
        <option [value]="20">20</option>
        <option [value]="50">50</option>
      </select>
    </div>
  </div>

  <div class="filters-bar">
    <input
      type="text"
      placeholder="Search"
      [value]="globalFilter()"
      (input)="onGlobalFilterChange($event)"
    />

    <button class="page-btn" (click)="clearFilters()">
      Clear
    </button>
  </div>

  <div class="alerts-card">
    @if (loading()) {
      <div class="state-message">Loading indications...</div>
    }

    @if (error()) {
      <div class="state-message error">{{ error() }}</div>
    }

    @if (!loading() && !error()) {
      <div class="table-wrapper">
        <table class="alerts-table">
          <thead>
          <tr>
            <th class="sortable-header" (click)="sortBy('id')">
              <span>ID</span>
              <span class="sort-icon">{{ getSortIcon('id') }}</span>
            </th>
            <th>Legal Name</th>
            <th class="sortable-header" (click)="sortBy('generatedIn')">
              <span>Generated In</span>
              <span class="sort-icon">{{ getSortIcon('generatedIn') }}</span>
            </th>
            <th>Type</th>
            <th>Latitude</th>
            <th>Longitude</th>
            <th>Involved Object</th>
            @if (hasAnyIndicationAction()) {
              <th>Actions</th>
            }
          </tr>
          </thead>

          <tbody>
            @if (paginatedIndications().length > 0) {
              @for (indication of paginatedIndications(); track indication.id) {
                <tr>
                  <td>{{ indication.id }}</td>
                  <td>{{ indication.legalName }}</td>
                  <td>{{ indication.generatedIn | date:'yyyy-MM-dd HH:mm:ss' }}</td>
                  <td>
                    @if (hasDetails(indication)) {
                      <button
                        type="button"
                        class="badge badge-button indication"
                        (click)="openDetailsModal(indication)"
                      >
                        {{ indication.indicationType }}
                      </button>
                    } @else {
                      <span class="badge">{{ indication.indicationType }}</span>
                    }
                  </td>
                  <td>{{ indication.latitude ?? '-' }}</td>
                  <td>{{ indication.longitude ?? '-' }}</td>
                  <td>
                    @if (hasInvolvedObject(indication)) {
                      <button
                        type="button"
                        class="involved-object-btn"
                        (click)="openInvolvedObjectModal(indication)"
                      >
                        {{ getInvolvedObjectButtonLabel(indication) }}
                      </button>
                    } @else {
                      <span>-</span>
                    }
                  </td>
                  @if (hasAnyIndicationAction()) {
                    <td>
                      <button class="delete-btn" (click)="openDeleteModal(indication.id)">
                        Delete
                      </button>
                    </td>
                  }
                </tr>
              }
            } @else {
              <tr>
                <td colspan="8" class="empty-row">No indications found.</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }

    @if (!loading() && totalItems() > 0) {
      <div class="pagination">
        <div class="pagination-info">
          Showing
          {{ (currentPage() - 1) * pageSize() + 1 }}
          -
          {{ Math.min(currentPage() * pageSize(), totalItems()) }}
          of
          {{ totalItems() }} indications
        </div>

        <div class="pagination-controls">
          <button
            class="page-btn"
            (click)="previousPage()"
            [disabled]="currentPage() === 1"
          >
            Previous
          </button>

          @for (page of pageNumbers(); track page) {
            <button
              class="page-btn"
              (click)="goToPage(page)"
              [class.active]="page === currentPage()"
            >
              {{ page }}
            </button>
          }

          <button
            class="page-btn"
            (click)="nextPage()"
            [disabled]="currentPage() === totalPages()"
          >
            Next
          </button>
        </div>
      </div>
    }
  </div>

  <div class="alerts-header assigned-alerts-header">
    <h1>Assigned Alerts</h1>

    <div class="header-actions">
      <label for="assignedAlertsPageSize">Rows per page:</label>
      <select id="assignedAlertsPageSize" (change)="onAssignedAlertsPageSizeChange($event)">
        <option [value]="5">5</option>
        <option [value]="10">10</option>
        <option [value]="20">20</option>
        <option [value]="50">50</option>
      </select>
    </div>
  </div>

  <div class="filters-bar">
    <input
      type="text"
      placeholder="Search assigned alerts"
      [value]="assignedAlertsSearch()"
      (input)="onAssignedAlertsSearchChange($event)"
    />

    <button
      class="page-btn"
      [class.active]="assignedAlertsScope() === 'all'"
      (click)="setAssignedAlertsScope('all')"
    >
      All assigned
    </button>

    <button
      class="page-btn"
      [class.active]="assignedAlertsScope() === 'me'"
      (click)="setAssignedAlertsScope('me')"
    >
      Assigned to me
    </button>

    <button
      class="page-btn"
      [class.active]="assignedAlertsScope() === 'organizations'"
      (click)="setAssignedAlertsScope('organizations')"
    >
      Assigned to my organization
    </button>

    <button class="page-btn" (click)="clearAssignedAlertsFilters()">
      Clear
    </button>
  </div>

  <div class="alerts-card">
    @if (assignedAlertsLoading()) {
      <div class="state-message">Loading assigned alerts...</div>
    }

    @if (assignedAlertsError()) {
      <div class="state-message error">{{ assignedAlertsError() }}</div>
    }

    @if (statusError() && !statusModalOpen()) {
      <div class="state-message error">{{ statusError() }}</div>
    }

    @if (!assignedAlertsLoading() && !assignedAlertsError()) {
      <div class="table-wrapper">
        <table class="alerts-table assigned-alerts-table">
          <thead>
          <tr>
            <th class="sortable-header" (click)="sortAssignedAlertsBy('id')">
              <span>ID</span>
              <span class="sort-icon">{{ getAssignedAlertsSortIcon('id') }}</span>
            </th>
            <th>Legal Name</th>
            <th class="sortable-header" (click)="sortAssignedAlertsBy('generatedIn')">
              <span>Generated In</span>
              <span class="sort-icon">{{ getAssignedAlertsSortIcon('generatedIn') }}</span>
            </th>
            <th>Type</th>
            <th>Status</th>
            <th>Latitude</th>
            <th>Longitude</th>
            <th>Involved Object</th>
            <th>Last Comment</th>
            <th>Assigned Users</th>
            <th>Assigned Organizations</th>
            <th>Actions</th>
          </tr>
          </thead>

          <tbody>
            @if (paginatedAssignedAlerts().length > 0) {
              @for (alert of paginatedAssignedAlerts(); track alert.id) {
                <tr [class.alert-row-highlighted]="alert.id === highlightedAlertId()">
                  <td>{{ alert.id }}</td>
                  <td>{{ alert.legalName }}</td>
                  <td>{{ alert.generatedIn | date:'yyyy-MM-dd HH:mm:ss' }}</td>
                  <td>
                    @if (assignedAlertHasDetails(alert)) {
                      <button
                        type="button"
                        class="badge badge-button"
                        [ngClass]="getAssignedAlertTypeLabel(alert).toLowerCase()"
                        (click)="openAssignedAlertDetailsModal(alert)"
                      >
                        {{ getAssignedAlertTypeLabel(alert) }}
                      </button>
                    } @else {
                      <span class="badge">{{ getAssignedAlertTypeLabel(alert) }}</span>
                    }
                  </td>
                  <td>
                    <span class="status-badge" [ngClass]="'status-' + (alert.status || 'NEW').toLowerCase()">
                      {{ getStatusLabel(alert.status) }}
                    </span>
                  </td>
                  <td>{{ alert.latitude ?? '-' }}</td>
                  <td>{{ alert.longitude ?? '-' }}</td>
                  <td>
                    @if (assignedAlertHasInvolvedObject(alert)) {
                      <button
                        type="button"
                        class="involved-object-btn"
                        (click)="openAssignedAlertInvolvedObjectModal(alert)"
                      >
                        {{ getAssignedAlertInvolvedObjectButtonLabel(alert) }}
                      </button>
                    } @else {
                      <span>-</span>
                    }
                  </td>
                  <td>{{ getLastCommentLabel(alert) }}</td>
                  <td>{{ getAssignedUsersLabel(alert) }}</td>
                  <td>{{ getAssignedOrganizationsLabel(alert) }}</td>
                  <td>
                    <div class="alert-action-row">
                      <button class="audit-btn" (click)="openAuditModal(alert)">
                        Audit
                      </button>
                      <button class="comment-btn" (click)="openCommentModal(alert)">
                        Comment
                      </button>
                      @if (canGiveFeedback(alert)) {
                        <button class="feedback-btn" (click)="openFeedbackModal(alert)">
                          Feedback
                        </button>
                      }
                      @if (canChangeStatus(alert)) {
                        <button class="status-btn" (click)="openStatusModal(alert)">
                          Change Status
                        </button>
                      }
                      @if (canAcknowledge(alert)) {
                        <button class="ack-btn" (click)="acknowledgeAlert(alert)" [disabled]="statusSaving()">
                          Acknowledge
                        </button>
                      }
                    </div>
                  </td>
                </tr>
              }
            } @else {
              <tr>
                <td colspan="12" class="empty-row">No assigned alerts found.</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }

    @if (!assignedAlertsLoading() && assignedAlertsTotalItems() > 0) {
      <div class="pagination">
        <div class="pagination-info">
          Showing
          {{ (assignedAlertsCurrentPage() - 1) * assignedAlertsPageSize() + 1 }}
          -
          {{ Math.min(assignedAlertsCurrentPage() * assignedAlertsPageSize(), assignedAlertsTotalItems()) }}
          of
          {{ assignedAlertsTotalItems() }} assigned alerts
        </div>

        <div class="pagination-controls">
          <button
            class="page-btn"
            (click)="previousAssignedAlertsPage()"
            [disabled]="assignedAlertsCurrentPage() === 1"
          >
            Previous
          </button>

          @for (page of assignedAlertsPageNumbers(); track page) {
            <button
              class="page-btn"
              (click)="goToAssignedAlertsPage(page)"
              [class.active]="page === assignedAlertsCurrentPage()"
            >
              {{ page }}
            </button>
          }

          <button
            class="page-btn"
            (click)="nextAssignedAlertsPage()"
            [disabled]="assignedAlertsCurrentPage() === assignedAlertsTotalPages()"
          >
            Next
          </button>
        </div>
      </div>
    }
  </div>

  <div class="grafana-section">
    <app-grafana-embed
      [src]="grafanaAlerts"
      [height]="900"
    ></app-grafana-embed>
  </div>
</div>

@if (deleteModalOpen()) {
  <div class="modal-backdrop" (click)="closeDeleteModal()">
    <div class="confirm-modal" (click)="$event.stopPropagation()">
      <div class="confirm-modal-header">
        <h2>Delete indication</h2>
      </div>

      <div class="confirm-modal-body">
        <p>Are you sure you want to delete this indication?</p>
        <p class="modal-warning">This action cannot be undone.</p>
      </div>

      <div class="confirm-modal-actions">
        <button class="modal-btn secondary" (click)="closeDeleteModal()">
          Cancel
        </button>
        <button class="modal-btn danger" (click)="confirmDelete()">
          Delete
        </button>
      </div>
    </div>
  </div>
}

@if (auditModalOpen() && selectedAuditAlert(); as alert) {
  <div class="modal-backdrop" (click)="closeAuditModal()">
    <div class="details-modal audit-modal" (click)="$event.stopPropagation()">
      <div class="confirm-modal-header">
        <h2>Audit for alert #{{ alert.id }}</h2>
      </div>

      <div class="details-modal-body">
        @if (auditLoading()) {
          <div class="state-message">Loading audit...</div>
        }

        @if (auditError()) {
          <div class="state-message error">{{ auditError() }}</div>
        }

        @if (!auditLoading() && !auditError()) {
          @if (auditEntries().length) {
            <div class="audit-list">
              @for (entry of auditEntries(); track entry.id) {
                <p class="audit-entry">
                  @if (entry.actionType === 'STATUS_CHANGED') {
                    <strong>{{ entry.performedByUsername }}</strong>
                    changed this alert status from
                    <strong>{{ getStatusLabel(entry.oldStatus) }}</strong>
                    to
                    <strong>{{ getStatusLabel(entry.newStatus) }}</strong>
                    on
                    <strong>{{ entry.createdAt | date:'yyyy-MM-dd HH:mm:ss' }}</strong>.
                  } @else if (entry.actionType === 'COMMENTED') {
                    <strong>{{ entry.performedByUsername }}</strong>
                    commented
                    <strong>"{{ entry.commentText }}"</strong>
                    on
                    <strong>{{ entry.createdAt | date:'yyyy-MM-dd HH:mm:ss' }}</strong>.
                  } @else if (entry.actionType === 'FEEDBACK_GIVEN') {
                    <strong>{{ entry.performedByUsername }}</strong>
                    gave feedback with rating
                    <strong>{{ entry.feedbackRating }}/5</strong>:
                    <strong>"{{ entry.feedbackMessage }}"</strong>
                    on
                    <strong>{{ entry.createdAt | date:'yyyy-MM-dd HH:mm:ss' }}</strong>.
                  } @else {
                    <strong>{{ entry.performedByUsername }}</strong>
                    {{ getAuditActionLabel(entry) }}
                    <strong>{{ getAuditTargetLabel(entry) }} {{ entry.targetName }}</strong>
                    on
                    <strong>{{ entry.createdAt | date:'yyyy-MM-dd HH:mm:ss' }}</strong>.
                  }
                </p>
              }
            </div>
          } @else {
            <p class="empty-details">No audit entries found for this alert.</p>
          }
        }
      </div>

      <div class="confirm-modal-actions">
        <button class="modal-btn secondary" (click)="closeAuditModal()">
          Close
        </button>
      </div>
    </div>
  </div>
}

@if (commentModalOpen() && selectedCommentAlert(); as alert) {
  <div class="modal-backdrop" (click)="closeCommentModal()">
    <div class="confirm-modal comment-modal" (click)="$event.stopPropagation()">
      <div class="confirm-modal-header">
        <h2>Comment on alert #{{ alert.id }}</h2>
      </div>

      <div class="confirm-modal-body">
        @if (commentError()) {
          <div class="state-message error">{{ commentError() }}</div>
        }

        <textarea
          class="modal-textarea"
          placeholder="Write a comment"
          [value]="commentText()"
          (input)="commentText.set($any($event.target).value)"
        ></textarea>
      </div>

      <div class="confirm-modal-actions">
        <button class="modal-btn secondary" (click)="closeCommentModal()" [disabled]="commentSaving()">Cancel</button>
        <button class="modal-btn primary" (click)="saveComment()" [disabled]="commentSaving()">Save</button>
      </div>
    </div>
  </div>
}

@if (feedbackModalOpen() && selectedFeedbackAlert(); as alert) {
  <div class="modal-backdrop" (click)="closeFeedbackModal()">
    <div class="confirm-modal feedback-modal" (click)="$event.stopPropagation()">
      <div class="confirm-modal-header">
        <h2>Feedback for alert #{{ alert.id }}</h2>
      </div>

      <div class="confirm-modal-body">
        @if (feedbackLoading()) {
          <div class="state-message">Loading feedback...</div>
        }

        @if (feedbackError()) {
          <div class="state-message error">{{ feedbackError() }}</div>
        }

        @if (!feedbackLoading()) {
          @if (existingFeedback(); as feedback) {
            <p class="feedback-existing">
              You already submitted feedback with rating <strong>{{ feedback.rating }}/5</strong>:
              <strong>"{{ feedback.message }}"</strong>
            </p>
          } @else {
            <div class="feedback-rating-label">Give a star note</div>
            <div class="stars">
              @for (rating of [1, 2, 3, 4, 5]; track rating) {
                <button
                  type="button"
                  class="star-btn"
                  [class.active]="feedbackRating() >= rating"
                  (click)="setFeedbackRating(rating)"
                >
                  &#9733;
                </button>
              }
            </div>

            <textarea
              class="modal-textarea"
              placeholder="Write feedback"
              [value]="feedbackMessage()"
              (input)="feedbackMessage.set($any($event.target).value)"
            ></textarea>
          }
        }
      </div>

      <div class="confirm-modal-actions">
        <button class="modal-btn secondary" (click)="closeFeedbackModal()" [disabled]="feedbackSaving()">Close</button>
        @if (!existingFeedback()) {
          <button class="modal-btn primary" (click)="saveFeedback()" [disabled]="feedbackSaving() || feedbackLoading()">Save</button>
        }
      </div>
    </div>
  </div>
}

@if (statusModalOpen() && selectedStatusAlert(); as alert) {
  <div class="modal-backdrop" (click)="closeStatusModal()">
    <div class="confirm-modal status-modal" (click)="$event.stopPropagation()">
      <div class="confirm-modal-header">
        <h2>Change status for alert #{{ alert.id }}</h2>
      </div>

      <div class="confirm-modal-body">
        @if (statusError()) {
          <div class="state-message error">{{ statusError() }}</div>
        }

        <div class="status-form">
          <label for="alertStatus">Status</label>
          <select id="alertStatus" [value]="selectedStatus()" (change)="onStatusSelectionChange($event)">
            @for (status of selectableStatuses; track status) {
              <option [value]="status">{{ getStatusLabel(status) }}</option>
            }
          </select>
        </div>
      </div>

      <div class="confirm-modal-actions">
        <button class="modal-btn secondary" (click)="closeStatusModal()" [disabled]="statusSaving()">
          Cancel
        </button>
        <button class="modal-btn primary" (click)="saveStatusChange()" [disabled]="statusSaving()">
          Save
        </button>
      </div>
    </div>
  </div>
}

@if (assignedAlertDetailsModalOpen() && selectedAssignedAlert(); as alert) {
  <div class="modal-backdrop" (click)="closeAssignedAlertDetailsModal()">
    <div class="details-modal" (click)="$event.stopPropagation()">
      <div class="confirm-modal-header">
        <h2>
          @if (alert.anomaly) {
            Anomaly details
          } @else if (alert.intentions.length) {
            Intentions details
          } @else {
            Alert details
          }
        </h2>
      </div>

      <div class="details-modal-body">
        @if (alert.anomaly; as anomaly) {
          <div class="details-list">
            <div class="details-list-item">
              <div class="detail-row">
                <span class="detail-label">Anomaly type</span>
                <span class="detail-value">{{ anomaly.anomalyType }}</span>
              </div>

              <div class="detail-row">
                <span class="detail-label">Anomaly score</span>
                <span class="detail-value">{{ anomaly.anomalyScore * 100 }}%</span>
              </div>

              <div class="detail-row">
                <span class="detail-label">Explanation</span>
                <span class="detail-value">{{ anomaly.explanation }}</span>
              </div>
            </div>
          </div>
        } @else if (alert.intentions.length) {
          <div class="details-list">
            @for (intention of alert.intentions; track $index) {
              <div class="details-list-item">
                <div class="detail-row">
                  <span class="detail-label">Classification</span>
                  <span class="detail-value">{{ intention.classification }}</span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">Probability</span>
                  <span class="detail-value">{{ intention.probability * 100 }}%</span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">Explanation</span>
                  <span class="detail-value">{{ intention.explanation }}</span>
                </div>
              </div>
            }
          </div>
        } @else {
          <p class="empty-details">No details available.</p>
        }
      </div>

      <div class="confirm-modal-actions">
        <button class="modal-btn secondary" (click)="closeAssignedAlertDetailsModal()">
          Close
        </button>
      </div>
    </div>
  </div>
}

@if (detailsModalOpen() && selectedIndication(); as indication) {
  <div class="modal-backdrop" (click)="closeDetailsModal()">
    <div class="details-modal" (click)="$event.stopPropagation()">
      <div class="confirm-modal-header">
        <h2>Indication details</h2>
      </div>

      <div class="details-modal-body">
        <div class="details-list">
          <div class="details-list-item">
            <div class="detail-row">
              <span class="detail-label">Type</span>
              <span class="detail-value">{{ indication.indicationType || '-' }}</span>
            </div>

            <div class="detail-row">
              <span class="detail-label">Value</span>
              <span class="detail-value">{{ indication.indicationValue || '-' }}</span>
            </div>

            <div class="detail-row">
              <span class="detail-label">Explanation</span>
              <span class="detail-value">{{ indication.explanation || '-' }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="confirm-modal-actions">
        <button class="modal-btn secondary" (click)="closeDetailsModal()">
          Close
        </button>
      </div>
    </div>
  </div>
}

@if (involvedObjectModalOpen()) {
  <div class="modal-backdrop" (click)="closeInvolvedObjectModal()">
    <div class="details-modal" (click)="$event.stopPropagation()">
      <div class="confirm-modal-header">
        <h2>Involved object details</h2>
      </div>

      <div class="details-modal-body">
        @if (involvedObjectLoading()) {
          <div class="state-message">Loading details...</div>
        }

        @if (involvedObjectError()) {
          <div class="state-message error">{{ involvedObjectError() }}</div>
        }

        @if (!involvedObjectLoading() && !involvedObjectError()) {
          @if (selectedInvolvedObjectType() === 'vessel' && selectedVessel(); as vessel) {
            <div class="details-list">
              <div class="details-list-item">
                <div class="detail-row">
                  <span class="detail-label">Type</span>
                  <span class="detail-value">Vessel</span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">ID</span>
                  <span class="detail-value">{{ vessel.id }}</span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">Legal Name</span>
                  <span class="detail-value">{{ vessel.legalName ?? '-' }}</span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">Generated In</span>
                  <span class="detail-value">
                    {{ vessel.generatedIn ? (vessel.generatedIn | date:'yyyy-MM-dd HH:mm:ss') : '-' }}
                  </span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">UUID</span>
                  <span class="detail-value">{{ vessel.uuid ?? '-' }}</span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">Latitude</span>
                  <span class="detail-value">{{ vessel.latitude ?? '-' }}</span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">Longitude</span>
                  <span class="detail-value">{{ vessel.longitude ?? '-' }}</span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">Name</span>
                  <span class="detail-value">{{ vessel.name ?? '-' }}</span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">COG</span>
                  <span class="detail-value">{{ vessel.cog ?? '-' }}</span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">Heading</span>
                  <span class="detail-value">{{ vessel.heading ?? '-' }}</span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">Speed</span>
                  <span class="detail-value">{{ vessel.speed ?? '-' }}</span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">Breadth</span>
                  <span class="detail-value">{{ vessel.breadth ?? '-' }}</span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">Callsign</span>
                  <span class="detail-value">{{ vessel.callsign ?? '-' }}</span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">Depth</span>
                  <span class="detail-value">{{ vessel.depth ?? '-' }}</span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">Draught</span>
                  <span class="detail-value">{{ vessel.draught ?? '-' }}</span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">Length</span>
                  <span class="detail-value">{{ vessel.length ?? '-' }}</span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">MMSI</span>
                  <span class="detail-value">{{ vessel.mmsi ?? '-' }}</span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">Navigational Status</span>
                  <span class="detail-value">{{ vessel.navigationalStatus ?? '-' }}</span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">AIS Ship Type</span>
                  <span class="detail-value">{{ vessel.aisShipType ?? '-' }}</span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">Source Type</span>
                  <span class="detail-value">{{ vessel.sourceType ?? '-' }}</span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">Track ID</span>
                  <span class="detail-value">{{ vessel.trackId ?? '-' }}</span>
                </div>
              </div>
            </div>
          }

          @if (selectedInvolvedObjectType() === 'object' && selectedObject(); as objectDetails) {
            <div class="details-list">
              <div class="details-list-item">
                <div class="detail-row">
                  <span class="detail-label">Type</span>
                  <span class="detail-value">Object</span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">ID</span>
                  <span class="detail-value">{{ objectDetails.id }}</span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">Legal Name</span>
                  <span class="detail-value">{{ objectDetails.legalName ?? '-' }}</span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">Generated In</span>
                  <span class="detail-value">
                    {{ objectDetails.generatedIn ? (objectDetails.generatedIn | date:'yyyy-MM-dd HH:mm:ss') : '-' }}
                  </span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">UUID</span>
                  <span class="detail-value">{{ objectDetails.uuid ?? '-' }}</span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">Latitude</span>
                  <span class="detail-value">{{ objectDetails.latitude ?? '-' }}</span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">Longitude</span>
                  <span class="detail-value">{{ objectDetails.longitude ?? '-' }}</span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">Location Uncertainty</span>
                  <span class="detail-value">{{ objectDetails.locationUncertainty ?? '-' }}</span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">Source Type</span>
                  <span class="detail-value">{{ objectDetails.sourceType ?? '-' }}</span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">Track ID</span>
                  <span class="detail-value">{{ objectDetails.trackId ?? '-' }}</span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">Category</span>
                  <span class="detail-value">{{ objectDetails.category ?? '-' }}</span>
                </div>
              </div>
            </div>
          }
        }
      </div>

      <div class="confirm-modal-actions">
        <button class="modal-btn secondary" (click)="closeInvolvedObjectModal()">
          Close
        </button>
      </div>
    </div>
  </div>
}
